import React, { useState, useEffect } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';
import {
    Phone, Clock, Users, CheckCircle2, TrendingUp, PhoneCall, Activity
} from 'lucide-react';
import apiService from '../../services/apiService';
import { useBranding } from '../../context/BrandingContext';

// --- ROBUST LIGHT MODE COMPONENTS (Internal to avoid dependency issues) ---

const Card = ({ children, className = "" }) => (
    <div className={`bg-white border border-slate-200 rounded-lg shadow-sm ${className}`}>
        {children}
    </div>
);

const CardContent = ({ children, className = "" }) => (
    <div className={`p-6 ${className}`}>
        {children}
    </div>
);

const MetricCard = ({ title, value, subtext, icon: Icon }) => (
    <Card className="hover:shadow-md transition-shadow duration-200">
        <CardContent>
            <div className="flex justify-between items-start">
                <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-500">{title}</p>
                    <h3 className="text-2xl font-bold text-slate-900 tracking-tight">{value}</h3>
                    {subtext && (
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                            {subtext}
                        </p>
                    )}
                </div>
                <div className="p-2 bg-slate-50 rounded-lg border border-slate-100">
                    <Icon className="w-5 h-5 text-slate-700" />
                </div>
            </div>
        </CardContent>
    </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white border border-slate-200 p-3 rounded-lg shadow-xl text-slate-900 z-50">
                <p className="font-semibold mb-1 text-sm text-slate-700">{label}</p>
                {payload.map((entry, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-slate-600">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                        <span className="capitalize">{entry.name}:</span>
                        <span className="font-medium text-slate-900">{entry.value}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const VoiceAnalytics = () => {
    const brandingContext = useBranding();
    const isFitenko = Boolean(
        brandingContext?.isFitenko ||
        brandingContext?.branding?.instance_name === 'fitenko' ||
        (typeof window !== 'undefined' && (
            window.location.hostname.includes('fitenko') ||
            localStorage.getItem('tenant_id') === 'fitenko' ||
            localStorage.getItem('lastTenant') === 'fitenko'
        ))
    );

    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(7);
    const [activeTab, setActiveTab] = useState('volume');

    useEffect(() => {
        const fetchStats = async () => {
            setLoading(true);
            try {
                const data = await apiService.getVoiceAnalytics(days);
                setStats(data);
            } catch (error) {
                console.error("Failed to load voice stats", error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, [days]);

    if (loading) return (
        <div className="flex items-center justify-center p-12 text-slate-400">
            <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${isFitenko ? 'border-[#C8A653]' : 'border-slate-600'}`}></div>
        </div>
    );

    if (!stats) return <div className="p-12 text-center text-slate-500">Failed to load analytics data.</div>;

    // Clean Palette
    const COLORS = {
        POSITIVE: isFitenko ? '#0B8B5E' : '#10b981',
        NEUTRAL: isFitenko ? '#687077' : '#64748b',
        NEGATIVE: isFitenko ? '#C84842' : '#ef4444',
        DEFAULT: isFitenko ? '#C8A653' : '#3b82f6'
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-500">

            {/* Header & Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                        Voice Performance
                    </h2>
                    <p className="text-slate-500 text-sm mt-1">
                        Real-time AI Interaction Analytics
                    </p>
                </div>

                <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
                    {[7, 30, 90].map(d => (
                        <button
                            key={d}
                            onClick={() => setDays(d)}
                            className={`
                                px-3 py-1.5 rounded-md text-sm font-medium transition-all
                                ${days === d
                                    ? 'bg-white text-slate-900 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                                }
                            `}
                        >
                            {d} Days
                        </button>
                    ))}
                </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard
                    title="Total Calls"
                    value={stats.overview.total_calls}
                    icon={Phone}
                    subtext="Inbound & Outbound"
                />
                <MetricCard
                    title="Avg Duration"
                    value={stats.overview.avg_duration}
                    icon={Clock}
                    subtext="Active Talk Time"
                />
                <MetricCard
                    title="Conversion Rate"
                    value={`${stats.overview.conversion_rate}%`}
                    icon={CheckCircle2}
                    subtext="Appointments Set"
                />
                <MetricCard
                    title="Unique Callers"
                    value={stats.overview.unique_callers}
                    icon={Users}
                    subtext="Distinct Leads"
                />
            </div>

            {/* Charts Area */}
            <Card>
                <div className="border-b border-slate-100 p-4">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <h3 className="text-lg font-semibold text-slate-900">Analytics Trends</h3>
                        <div className="flex bg-slate-100 p-1 rounded-lg">
                            {['volume', 'sentiment', 'outcomes', 'hourly'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`
                                        px-3 py-1.5 rounded-md text-sm font-medium transition-all capitalize
                                        ${activeTab === tab
                                            ? 'bg-white text-slate-900 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                        }
                                    `}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    {/* VOLUME CHART */}
                    {activeTab === 'volume' && (
                        <div className="h-[350px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={stats.volume} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={isFitenko ? "#C8A653" : "#3b82f6"} stopOpacity={0.2} />
                                            <stop offset="95%" stopColor={isFitenko ? "#C8A653" : "#3b82f6"} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                    <XAxis
                                        dataKey="date"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#64748b', fontSize: 12 }}
                                        dy={10}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#64748b', fontSize: 12 }}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Area
                                        type="monotone"
                                        dataKey="count"
                                        stroke={isFitenko ? "#C8A653" : "#3b82f6"}
                                        strokeWidth={2}
                                        fill="url(#colorCount)"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* SENTIMENT CHART */}
                    {activeTab === 'sentiment' && (
                        <div className="h-[350px] w-full flex justify-center">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={stats.sentiment}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={80}
                                        outerRadius={110}
                                        paddingAngle={2}
                                        dataKey="value"
                                    >
                                        {stats.sentiment.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[entry.name] || COLORS.DEFAULT} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* OUTCOMES CHART */}
                    {activeTab === 'outcomes' && (
                        <div className="h-[350px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={stats.outcomes} layout="vertical" margin={{ left: 40, right: 40 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                                    <XAxis type="number" hide />
                                    <YAxis
                                        dataKey="name"
                                        type="category"
                                        axisLine={false}
                                        tickLine={false}
                                        width={100}
                                        tick={{ fill: '#475569', fontSize: 12, fontWeight: 500 }}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={32}>
                                        {stats.outcomes.map((entry, index) => (
                                             <Cell key={`cell-${index}`} fill={isFitenko ? "#C8A653" : "#3b82f6"} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* HOURLY CHART */}
                    {activeTab === 'hourly' && (
                        <div className="h-[350px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={stats.hourly}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                    <XAxis
                                        dataKey="hour"
                                        axisLine={false}
                                        tickLine={false}
                                        tickFormatter={(val) => `${val}:00`}
                                        tick={{ fill: '#64748b', fontSize: 12 }}
                                        dy={10}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#64748b', fontSize: 12 }}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar dataKey="count" fill={isFitenko ? "#A97F2E" : "#8b5cf6"} radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default VoiceAnalytics;
